import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { useRestaurantDataQueries } from '@/routes/restaurant/useRestaurantData';

export const Route = createFileRoute('/r/$restaurantId')({
  beforeLoad: async ({ location, params }) => {
    const { restaurantId } = params;
    if (location.pathname === `/r/${restaurantId}`) {
      throw redirect({
        params: { restaurantId },
        to: '/r/$restaurantId/menu/menu-items',
      });
    }
  },
  component: RestaurantLayout,
});

/**
 * Renders the restaurant's pages once the restaurant, its menu, its POS config and its feature
 * flags have loaded, so this is their one loading and error screen. Inside it, `useRestaurant()`,
 * `useMenu()` and `usePosConfig()` return the data directly, and `useFeatureFlagEnabled()` never
 * reads `false` early.
 */
function RestaurantLayout() {
  const { restaurantId } = Route.useParams();
  const { restaurantQuery, menuQuery, posConfigQuery, featureFlagsQuery, restaurantPlanQuery } =
    useRestaurantDataQueries(restaurantId);

  if (restaurantQuery.isError) {
    return <ErrorAlert message={restaurantQuery.error.message} title="Error loading restaurant" />;
  }
  if (menuQuery.isError) {
    return <ErrorAlert message={menuQuery.error.message} title="Error loading menu" />;
  }
  if (posConfigQuery.isError) {
    return <ErrorAlert message={posConfigQuery.error.message} title="Error loading pos config" />;
  }
  if (featureFlagsQuery.isError) {
    return (
      <ErrorAlert message={featureFlagsQuery.error.message} title="Error loading feature flags" />
    );
  }
  if (restaurantPlanQuery.isError) {
    return <ErrorAlert message={restaurantPlanQuery.error.message} title="Error loading plan" />;
  }
  // Pages gate features on the flags, so they wait for them rather than show a feature as off.
  if (
    restaurantQuery.isPending ||
    menuQuery.isPending ||
    posConfigQuery.isPending ||
    featureFlagsQuery.isPending
  ) {
    return <LoadingIcon />;
  }

  // `getById` returns `null` when no restaurant is found for given id.
  // Block the child routes so menus, items, etc. can't be created against a dangling restaurant id.
  if (restaurantQuery.data === null) {
    return (
      <ErrorAlert
        message="We couldn't find a restaurant for this link. Check the URL and try again."
        title="Restaurant not found"
      />
    );
  }

  return <Outlet />;
}

// const excludeSuperAdminOnlyLinks = (links: NavLink[]): NavLink[] => {
//   return links
//     .filter((link) => !link.isSuperAdminOnly)
//     .map((link) => ({
//       ...link,
//       children: excludeSuperAdminOnlyLinks(link.children ?? []),
//     }));
// };
